import { useEffect, useRef, useState, useCallback } from 'react';
import { useWebSocket } from '../../../api/websocket/WebSocketContext';
import { Message } from '../../../api/websocket/types';
import { TaskMonitor, MonitoringState } from '../../../api/monitoring/TaskMonitor';

export type ProcessingState = 
  | { status: 'idle' }
  | { status: 'connecting' }
  | { status: 'active' }
  | { status: 'completed' }
  | { status: 'error', error: string };

export interface UseProcessingSocketProps {
  taskId: string;
  onMessage?: (message: Message) => void;
  onComplete?: () => void;
  onStageComplete?: (stageName: string, result: Record<string, unknown>) => void;
  onConnectionFailure?: () => void;
  onConnectionSuccess?: () => void;
}

export const useProcessingSocket = ({
  taskId,
  onMessage,
  onComplete,
  onStageComplete,
  onConnectionFailure,
  onConnectionSuccess,
}: UseProcessingSocketProps) => {
  const { instance: sharedWSManager } = useWebSocket();

  const [state, setState] = useState<ProcessingState>({ status: 'idle' });
  const monitorRef = useRef<TaskMonitor | null>(null);
  const previousParamsRef = useRef({ taskId: '' });

  // Debug logging
  const debugLog = useCallback((message: string, extra: Record<string, unknown> = {}) => {
    console.log(`[useProcessingSocket] ${message}`, {
      taskId,
      state,
      monitorExists: monitorRef.current ? 'yes' : 'no',
      sharedWSManager: sharedWSManager ? 'exists' : 'none',
      ...extra,
      timestamp: new Date().toISOString(),
    });
  }, [taskId, state, sharedWSManager]);

  // Map monitoring state to processing state
  const handleMonitoringStateChange = useCallback((monitoringState: MonitoringState) => {
    debugLog('Monitor state changed', { monitoringState });

    switch (monitoringState.type) {
      case 'initializing':
      case 'connecting_websocket':
        setState({ status: 'connecting' });
        break;
      case 'dual_monitoring':
      case 'websocket_only':
      case 'rest_only':
        setState({ status: 'active' });
        break;
      case 'completed':
        setState({ status: 'completed' });
        onComplete?.();
        break;
      case 'failed':
        // This is only for monitor failures (connection issues), not task failures
        const errorMessage = monitoringState.error.message || 'Connection failed';
        debugLog('Monitor failed', { error: errorMessage });
        setState({ status: 'error', error: errorMessage });
        onConnectionFailure?.();
        break;
    }
  }, [debugLog, onComplete, onConnectionFailure]);

  // Clean up resources
  const cleanup = useCallback(() => {
    debugLog('Cleaning up resources', {});
    if (monitorRef.current) {
      monitorRef.current.stop();
      monitorRef.current = null;
    }
  }, [debugLog]);

  // Setup monitoring
  const setupMonitoring = useCallback(async () => {
    debugLog('Setting up monitoring', {});

    try {
      const monitor = new TaskMonitor({
        taskId,
        onMessage,
        onComplete,
        onStageComplete,
        onError: (error) => {
          const errorMessage = error.message || 'Connection error occurred';
          debugLog('Connection error', { error: errorMessage });
          setState({ status: 'error', error: errorMessage });
          onConnectionFailure?.();
        },
        onConnectionStateChange: handleMonitoringStateChange
      }, sharedWSManager!);

      monitorRef.current = monitor;
      await monitor.start();
      onConnectionSuccess?.();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to setup monitoring';
      debugLog('Setup failed', { error: errorMessage });
      setState({ status: 'error', error: errorMessage });
      onConnectionFailure?.();
      throw error;
    }
  }, [
    taskId,
    debugLog,
    onMessage,
    onComplete,
    onStageComplete,
    onConnectionFailure,
    onConnectionSuccess,
    handleMonitoringStateChange,
    sharedWSManager
  ]);

  // Update the effect to watch only taskId changes
  useEffect(() => {
    debugLog('Effect (param change) triggered', {});
    if (taskId === previousParamsRef.current.taskId) {
      debugLog('TaskId unchanged, skipping reinitialization', {});
      return;
    }
    debugLog('TaskId changed', {
      previousTaskId: previousParamsRef.current.taskId,
      currentTaskId: taskId
    });
    previousParamsRef.current = { taskId };

    if (!taskId) {
      debugLog('Missing taskId, invoking cleanup', { taskId });
      cleanup();
      return;
    }

    debugLog('Cleaning up existing monitor before setting up new monitoring instance', {});
    cleanup();
    debugLog('Setting up new monitoring instance', {});
    setupMonitoring();
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  // Terminal state effect remains unchanged
  useEffect(() => {
    if (state.status === 'completed' || state.status === 'error') {
      debugLog('Terminal state detected, invoking cleanup', { stateStatus: state.status });
      cleanup();
    }
  }, [state.status, debugLog, cleanup]);

  return { state };
};

export default useProcessingSocket;